import { defineStore } from "pinia";
import * as api from "@/services/cuentas";
import { i18n } from "@/i18n";
import { medir, identificarVisita, marcarSiEsInterna } from "@/services/medicion";

// La sesión del oyente.
//
// Contrato: `CUENTAS_WEB_INSTRUCCIONES.md`. El token es opaco, dura 180 días y
// el contrato admite guardarlo en `localStorage` para esta fase.
//
// 🔴 **El `401` es la única fuente de verdad sobre la sesión.** No se comprueba
// la caducidad por nuestra cuenta ni se adivina si el token sigue vivo: se pide
// y, si el servidor dice que no, se cierra. Un token puede morir antes de sus
// 180 días —al cambiar la contraseña, por ejemplo, que revoca los demás—, así
// que cualquier cálculo local mentiría tarde o temprano.
const CLAVE = "astra_cuenta_token";

/** Lee el token guardado. Con el almacenamiento bloqueado, sesión vacía. */
function leerToken() {
  try {
    return localStorage.getItem(CLAVE) || "";
  } catch (error) {
    return "";
  }
}

function guardarToken(token) {
  try {
    if (token) localStorage.setItem(CLAVE, token);
    else localStorage.removeItem(CLAVE);
  } catch (error) {
    // Navegación privada o almacenamiento bloqueado: la sesión dura lo que dure
    // la pestaña. Es peor que nada, pero mucho mejor que romper el acceso.
  }
}

/**
 * El `motivo` de un `alta_error`, del código que responde el servidor.
 * Contrato de analítica §5, 15.09.26. Los valores son fijos: la pestaña Stats
 * los desglosa por este nombre exacto y uno distinto no lo ve.
 *
 * Un fallo de red no trae código —`services/cuentas.js` devuelve el error sin
 * `estado`— y cae en `otro`, como manda el contrato.
 */
function motivoDeAltaError(estado) {
  if (estado === 409) return "correo_registrado";
  if (estado === 429) return "limite";
  if (estado === 400) return "datos";
  return "otro";
}

/**
 * Qué hay DETRÁS de un `otro`, para que se pueda perseguir sin esperar a que
 * alguien nos preste su consola.
 *
 * 🔴 Añadido el 21.09.26. El servidor lleva dos mensajes pidiendo saber qué es
 * ese `otro` —`ALTAS_SIGUEN_FALLANDO_WEB.md`— y nosotros les decíamos «lo
 * apuntamos en la consola del navegador», que es tanto como no decir nada: esa
 * consola es de quien no consigue darse de alta, y no la vamos a ver nunca.
 *
 * **Es una propiedad de más, no un `motivo` nuevo**: los cuatro valores de
 * `motivo` siguen siendo los del contrato y su embudo no se entera. Aquí va
 * `sin_respuesta` cuando la petición ni llegó a contestar —red, CORS, un
 * intermediario que la corta— y `http_<código>` cuando contestó algo que no
 * esperábamos, que es lo que distingue un 502 de una caída de red.
 *
 * ⛔ Un código de estado no es un dato de nadie. Aquí no entra nada más.
 */
function detalleDeAltaError(estado, fallo) {
  if (estado) return `http_${estado}`;
  // 🔴 **Con el nombre del fallo detrás** desde el 24.09.26, a petición del
  // servidor: `sin_respuesta` decía que no llegó, pero no por qué.
  // `TypeError` es red o CORS, `AbortError` es que se fueron de la página.
  return fallo ? `sin_respuesta_${fallo}` : "sin_respuesta";
}

export const useCuentaStore = defineStore("cuenta", {
  state: () => ({
    token: leerToken(),
    cuenta: null,
    cargando: false,
    // El último error del servidor, ya legible. Lo pinta quien lo necesite.
    error: "",
  }),

  getters: {
    // Hay sesión cuando hay token. Que sea válido lo dirá el servidor.
    haySesion: (state) => !!state.token,

    // 🔴 **El tipo de cuenta lo dice el servidor**, en `cuenta.roles`, desde el
    // 13.09.26: `["oyente"]`, `["artista"]`, `["sello"]` o los dos. Son
    // exclusivos y **solo cuenta lo verificado**: una solicitud en curso o
    // descartada sigue siendo oyente. Antes se deducía de `perfiles`, y un perfil
    // descartado o en revisión salía como artista.
    //
    // ⚠️ **Es clasificación, no permiso.** Qué área se abre lo marcan los bloques
    // `ficha`, `sello` y `roster`: un sello en revisión es `["oyente"]` y ya usa
    // su área. Si el campo no llegara, se reconstruye con la misma regla.
    roles: (state) => {
      const r = state.cuenta?.roles;
      if (Array.isArray(r) && r.length) return r;
      const p = state.cuenta?.perfiles || {};
      const out = [];
      if (p.artista?.estado === "verificado") out.push("artista");
      if (p.sello?.estado === "verificado") out.push("sello");
      return out.length ? out : ["oyente"];
    },
    esOyente() {
      return this.roles.includes("oyente");
    },
    esArtista() {
      return this.roles.includes("artista");
    },
    esSello() {
      return this.roles.includes("sello");
    },

    // El estado de cada solicitud, para pintarlo tal cual. `null` si no hay.
    perfilArtista: (state) => state.cuenta?.perfiles?.artista || null,
    perfilSello: (state) => state.cuenta?.perfiles?.sello || null,

    // ⚠️ Sirve para el aviso de «verifica tu correo», que **no bloquea nada**:
    // se puede usar la cuenta sin verificar. Solo aparca las solicitudes de
    // perfil hasta que se verifique.
    correoVerificado: (state) => !!state.cuenta?.emailVerificado,
  },

  actions: {
    /** Guarda sesión y cuenta de una respuesta que trae las dos cosas. */
    aceptar(datos) {
      if (datos.token) {
        this.token = datos.token;
        guardarToken(datos.token);
      }
      if (datos.cuenta) this.conocer(datos.cuenta);
      this.error = "";
      return true;
    },

    /**
     * Guarda la cuenta que acaba de llegar y se lo cuenta a la analítica: el
     * tipo de cuenta de la visita, sin identificador, y la marca del personal
     * si la cuenta es interna. Contrato de analítica §3 y §4, 13.09.26.
     */
    conocer(cuenta) {
      this.cuenta = cuenta;
      marcarSiEsInterna(cuenta);
      identificarVisita(cuenta);
    },

    /** Cierra la sesión aquí. No hay endpoint de logout: el token se olvida. */
    salir() {
      this.token = "";
      this.cuenta = null;
      this.error = "";
      guardarToken("");
      identificarVisita(null);
    },

    async registrar(datos) {
      this.cargando = true;
      const r = await api.registrar(datos);
      this.cargando = false;
      // El mismo `tipo` en los dos eventos: el embudo compara un paso con el
      // otro y con dos formas de sacarlo acabarían no cuadrando.
      const tipo = datos?.perfil?.tipo || "oyente";
      if (!r.ok) {
        this.error = r.error;
        // 🔴 **Solo lo que responde el servidor.** Los avisos del propio
        // formulario —correo mal escrito, contraseña corta— no llegan hasta
        // aquí, y el contrato dice que no cuentan.
        //
        // ⛔ Ni el mensaje del error ni el correo salen en el evento: solo el
        // tipo y el motivo. El mensaje lo escribe el servidor y puede llevar
        // dentro lo que la persona tecleó.
        medir("alta_error", {
          tipo,
          motivo: motivoDeAltaError(r.estado),
          detalle: detalleDeAltaError(r.estado, r.fallo),
        });

        // 🔴 **Si no llegó, se intenta por formulario.** Consejo del servidor,
        // 24.09.26: «en vez de reintentar el mismo `fetch`, enviad el
        // formulario. Es un reintento que sí cambia algo». Y cambia: un envío de
        // formulario es una navegación, así que no pasa por `fetch`, ni por CORS,
        // ni por el service worker, que es donde muere esto en los navegadores
        // incrustados de Instagram y Android.
        //
        // ⚠️ **Se espera un momento antes de enviarlo.** La navegación corta lo
        // que esté en vuelo, y lo que está en vuelo es el `alta_error` de arriba:
        // sin esa pausa perderíamos justo la medida que nos dice cuántas veces
        // pasa. Ver `services/cuentas.js` § mandaElFormularioDeAlta.
        if (r.sinRespuesta && typeof window !== "undefined") {
          setTimeout(() => api.mandaElFormularioDeAlta(datos), 200);
        }

        return r;
      }
      this.aceptar(r);
      medir("alta_completada", { tipo });
      return r;
    },

    async entrar(datos) {
      this.cargando = true;
      const r = await api.entrar(datos);
      this.cargando = false;
      if (!r.ok) {
        this.error = r.error;
        return r;
      }
      this.aceptar(r);
      medir("login");
      return r;
    },

    /**
     * Trae la cuenta del servidor. Se llama al entrar al área, no se cachea
     * entre sesiones de navegación: lo pide el contrato.
     */
    async cargar() {
      if (!this.token) return { ok: false, error: i18n.global.t("errores.sinSesion") };
      this.cargando = true;
      const r = await api.yo(this.token);
      this.cargando = false;

      // Sesión fuera: el token no vale y quedarse con él solo sirve para
      // repetir el 401 en la siguiente pantalla.
      if (r.estado === 401) {
        this.salir();
        return r;
      }
      if (!r.ok) {
        this.error = r.error;
        return r;
      }
      this.conocer(r.cuenta);
      return r;
    },

    async actualizar(cambios) {
      const r = await api.actualizar(this.token, cambios);
      if (r.estado === 401) this.salir();
      else if (r.ok) this.cuenta = r.cuenta;
      else this.error = r.error;
      return r;
    },

    /**
     * ⚠️ Devuelve un token NUEVO y revoca los demás. Guardarlo no es opcional:
     * sin esto, la propia sesión que acaba de cambiar la contraseña se queda
     * fuera en la siguiente llamada.
     */
    async cambiarContrasena(datos) {
      const r = await api.cambiarContrasena(this.token, datos);
      if (r.estado === 401) this.salir();
      else if (r.ok) this.aceptar(r);
      else this.error = r.error;
      return r;
    },

    async solicitarPerfil(datos) {
      const r = await api.solicitarPerfil(this.token, datos);
      if (r.estado === 401) this.salir();
      else if (r.ok) {
        this.cuenta = r.cuenta;
        medir("perfil_solicitado", { tipo: datos?.tipo });
      }
      else this.error = r.error;
      return r;
    },

    // ── Las tres propuestas y el guardado directo ──
    //
    // Las cuatro devuelven la `cuenta` entera ya actualizada, así que se
    // guarda tal cual: pedir `/me` después sería una llamada de más y una
    // ventana en la que la pantalla enseña algo viejo.
    async proponerFicha(cambios) {
      const r = await api.proponerFicha(this.token, cambios);
      if (r.estado === 401) this.salir();
      else if (r.ok && r.cuenta) this.cuenta = r.cuenta;
      else if (!r.ok) this.error = r.error;
      return r;
    },

    async guardarSello(datos) {
      const r = await api.guardarSello(this.token, datos);
      if (r.estado === 401) this.salir();
      else if (r.ok && r.cuenta) this.cuenta = r.cuenta;
      else if (!r.ok) this.error = r.error;
      return r;
    },

    async proponerSello(datos) {
      const r = await api.proponerSello(this.token, datos);
      if (r.estado === 401) this.salir();
      else if (r.ok && r.cuenta) this.cuenta = r.cuenta;
      else if (!r.ok) this.error = r.error;
      return r;
    },

    async proponerRoster(datos) {
      const r = await api.proponerRoster(this.token, datos);
      if (r.estado === 401) this.salir();
      else if (r.ok && r.cuenta) this.cuenta = r.cuenta;
      else if (!r.ok) this.error = r.error;
      return r;
    },

    async pedirVerificacion() {
      const r = await api.pedirVerificacion(this.token);
      if (r.estado === 401) this.salir();
      else if (!r.ok) this.error = r.error;
      return r;
    },

    /** Baja suave: recuperable entrando de nuevo. El plazo lo fija el servidor. */
    async borrarCuenta() {
      const r = await api.borrarCuenta(this.token);
      if (r.ok || r.estado === 401) this.salir();
      else this.error = r.error;
      return r;
    },
  },
});
